/**
 * Configuração centralizada de URLs da aplicação
 * Detecta automaticamente o ambiente (localhost, test, produção)
 * e constrói as URLs dinamicamente baseado no hostname atual
 *
 * Lógica de resolução de domínio:
 * - Subdomínios de frontend conhecidos (v3, app, www) são removidos
 * - Subdomínios de ambiente (test, staging, etc.) são preservados
 * - O prefixo do serviço (backend, gateway) é adicionado ao domínio resultante
 *
 * Exemplos:
 * - v3.troiachat.com    → backend.troiachat.com       (v3 removido)
 * - test.troiachat.com  → backend.test.troiachat.com  (test preservado)
 * - v3.altta.ai         → backend.altta.ai            (v3 removido)
 * - app.x-club.ai       → backend.x-club.ai           (app removido)
 * - v3.phisontech.com.br→ backend.phisontech.com.br   (v3 removido)
 */

const isLocalhost = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

const multiPartTLDs = [
  'com.br', 'net.br', 'org.br', 'gov.br', 'edu.br', 'mil.br',
  'co.uk', 'ac.uk', 'gov.uk', 'org.uk', 'net.uk',
  'co.jp', 'ac.jp', 'go.jp', 'or.jp', 'ne.jp',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au',
  'co.za', 'org.za', 'net.za', 'gov.za', 'ac.za',
  'com.ar', 'gov.ar', 'org.ar', 'net.ar', 'edu.ar',
  'co.nz', 'net.nz', 'org.nz', 'govt.nz', 'ac.nz',
  'com.mx', 'org.mx', 'net.mx', 'edu.mx', 'gob.mx',
  'co.in', 'net.in', 'org.in', 'edu.in', 'gov.in',
];

/**
 * Subdomínios conhecidos como prefixo do frontend.
 * Estes são removidos ao construir URLs de serviços (backend, gateway).
 * Qualquer outro subdomínio (test, staging, etc.) é preservado como parte do domínio.
 */
const FRONTEND_SUBDOMAINS = ['v3', 'app', 'www'];

/**
 * Retorna o domínio para serviços (backend, gateway, widget).
 * Remove apenas subdomínios de frontend conhecidos, preservando subdomínios de ambiente.
 */
const getServiceDomain = (): string => {
  const hostname = window.location.hostname;
  if (isLocalhost()) return hostname;

  const parts = hostname.split('.');

  const last2Parts = parts.slice(-2).join('.');
  const isMultiPartTLD = multiPartTLDs.includes(last2Parts);
  const minParts = isMultiPartTLD ? 3 : 2;

  if (parts.length > minParts && FRONTEND_SUBDOMAINS.includes(parts[0])) {
    return parts.slice(1).join('.');
  }

  return hostname;
};

export const getApiBaseUrl = (): string => {
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL;
  if (isLocalhost()) return 'http://localhost:3000/api';
  return `https://backend.${getServiceDomain()}/api`;
};

export const getApiUrl = (): string => {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;
  if (isLocalhost()) return 'http://localhost:3000';
  return `https://backend.${getServiceDomain()}`;
};

export const getGatewayUrl = (): string => {
  if (import.meta.env.VITE_GATEWAY_URL) return import.meta.env.VITE_GATEWAY_URL;
  if (isLocalhost()) return 'http://localhost:3003';
  return `https://gateway.${getServiceDomain()}`;
};

/**
 * Configuração da aplicação
 */
export const config = {
  apiBaseUrl: getApiBaseUrl(),
  apiUrl: getApiUrl(),
  gatewayUrl: getGatewayUrl(),
  appName: import.meta.env.VITE_APP_NAME || 'TROIA',
  appVersion: import.meta.env.VITE_APP_VERSION || '3.0.0',
  isLocalhost: isLocalhost(),
};

export default config;
